import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, adminApi, ApiError, ADMIN_KEY, OWNER_EMAIL, TOKEN_KEY } from "@/lib/api";

const AuthCtx = createContext(null);
const AdminCtx = createContext(null);
const DraftCtx = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clear = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setToken(null);
    qc.cancelQueries();
    qc.clear();
  }, [qc]);

  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) return setLoading(false);
    api.get("/auth/me")
      .then((me) => { setUser(me); setToken(saved); })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    window.addEventListener("toto-session-expired", clear);
    return () => window.removeEventListener("toto-session-expired", clear);
  }, [clear]);

  const accept = useCallback((res) => {
    localStorage.removeItem(ADMIN_KEY);
    localStorage.setItem(TOKEN_KEY, res.token);
    setToken(res.token);
    setUser(res.user);
  }, []);
  const login = useCallback(async (identifier, password) => accept(await api.post("/auth/login", { identifier, password })), [accept]);
  const register = useCallback(async (input) => accept(await api.post("/auth/register", input)), [accept]);
  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem(ADMIN_KEY);
    clear();
  }, [clear]);

  const value = useMemo(() => ({ user, token, loading, login, register, logout, accept, setUser }), [user, token, loading, login, register, logout, accept]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onClear = () => setAdmin(null);
    window.addEventListener("toto-admin-cleared", onClear);
    if (!localStorage.getItem(ADMIN_KEY)) setReady(true);
    else adminApi.get("/admin/me")
      .then((me) => (me?.email?.toLowerCase() === OWNER_EMAIL ? setAdmin(me) : localStorage.removeItem(ADMIN_KEY)))
      .catch(() => localStorage.removeItem(ADMIN_KEY))
      .finally(() => setReady(true));
    return () => window.removeEventListener("toto-admin-cleared", onClear);
  }, []);

  const login = useCallback(async (email, password) => {
    const e = email.trim().toLowerCase();
    if (e !== OWNER_EMAIL) throw new ApiError("Admin access is reserved for the TOTO owner account.", 403);
    const res = await adminApi.post("/admin/login", { email: e, password });
    localStorage.setItem(ADMIN_KEY, res.token);
    setAdmin(res.admin);
  }, []);
  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_KEY);
    setAdmin(null);
  }, []);

  const value = useMemo(() => ({ admin, ready, login, logout }), [admin, ready, login, logout]);
  return <AdminCtx.Provider value={value}>{children}</AdminCtx.Provider>;
}

export function DraftProvider({ children }) {
  const [draft, setDraft] = useState(null);
  const value = useMemo(() => ({ draft, setDraft }), [draft]);
  return <DraftCtx.Provider value={value}>{children}</DraftCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const useAdminAuth = () => useContext(AdminCtx);
export const useScanDraft = () => useContext(DraftCtx);
